import type { Page, WebhookDelivery, WebhookDeliveryStatus } from '@servicetim/shared';
import { useMutation } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

/*
 * Webhook delivery log (`/admin/v1/webhooks/{id}/deliveries`, S-08-07): the attempt history for
 * one subscription and a manual retry, tenant admin only (mirrors `use-webhooks.ts`).
 */

const base = (subscriptionId: string) => adminPath(`/webhooks/${subscriptionId}/deliveries`);

export interface WebhookDeliveryFilters {
  status?: WebhookDeliveryStatus;
}

/** `GET /admin/v1/webhooks/{id}/deliveries?status=`, newest first. */
export function useWebhookDeliveries(
  subscriptionId: string | undefined,
  filters: WebhookDeliveryFilters = {},
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const { request, keys } = useApiContext();
  return usePagedList<WebhookDelivery>({
    queryKey: keys.webhooks.deliveries(subscriptionId ?? '', { ...filters, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<WebhookDelivery>>('GET', base(subscriptionId ?? ''), {
        query: { ...filters, ...pageQuery(cursor, pageSize) },
        signal,
      }),
    enabled: subscriptionId !== undefined,
  });
}

/** `POST /admin/v1/webhooks/{id}/deliveries/{did}/retry`: one more attempt, `failed` or `dead`. */
export function useRetryWebhookDelivery(subscriptionId: string | undefined) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (deliveryId: string) =>
      request<WebhookDelivery>('POST', `${base(subscriptionId ?? '')}/${deliveryId}/retry`),
    onSuccess: () => invalidate(keys.webhooks.deliveries(subscriptionId ?? ''), keys.webhooks.all),
  });
}
